import httpClient from '../../api/httpClient'
import { caseOf, debounce, fold, HTTP, map, Maybe, maybe, Union } from '../../lib'
import { reverse } from '../../lib/types/Array'
import Status from '../../lib/types/Status'
import { update } from '../../lib/vuex'
import router from '../../router'
import { errorHandler, makeDecimalValue, oneTimeEarningsMap } from '../../utils/helper'

const serializeUserUpdate = (user) => ({
  username: user.username,
  metapic_client_id: user.client_id,
  revenue_tier_id: user.revenue_tier_id,
  is_verified: user.is_verified,
  is_suspended: user.is_suspended,
  payment_blocked: user.payment_blocked,
  remove_earnings: user.remove_earnings,
})

export const ToolbarState = Union('ToolbarState', {
  Idle: [],
  ShowSettings: [],
  ShowStats: [],
  ShowConvertLink: [],
  ShowAddTags: [],
  Pending: [],
})

export const DeepLinkStatus = Union('DeepLinkStatus', {
  Idle: [],
  Processing: [],
  Done: [],
})

const socialMedia = (user) =>
  (user.social_media || []).length === 0
    ? [{ id: 0, type: 'blog', identifier: '' }]
    : map(user.social_media, (account, i) => ({
        ...account,
        remoteId: account.id,
        id: i,
      }))

const findUsers = (commit, state, query) =>
  httpClient
    .get(`/users/find`, { params: { query } })
    .then((response) => {
      query === state.usersList.value &&
        commit('updateUsersList', {
          path: 'suggestions',
          value: map(response.data.data, (user) => ({
            ...user,
            name: `${user.id}${user.email ? ` - ${user.email}` : ''}`,
            one_time_earnings: oneTimeEarningsMap(user.one_time_earnings ?? []),
          })),
        })
    })
    .catch((error) => console.error(error))

findUsers.delayed = debounce(findUsers, 250)

export default {
  state: {
    originalUser: {},
    user: HTTP.Request.Inactive(),
    updateStatus: Status.Idle(),
    clients: {},
    cheatReport: Maybe.None(),
    CheatReportFilters: { startDate: '', endDate: '' },
    comments: {},
    oneTimeEarnings: [],
    oneTimeEarningsOriginal: [],
    usersList: {
      suggestions: [],
      value: '',
      selected: {},
    },
    mergeUsersStatus: HTTP.Request.Inactive(),
    errorMessageList: [],
    isFetchingCheatReports: false,
  },

  mutations: {
    setOriginalUser: (state, user) => {
      state.originalUser = {
        ...user,
        client_id: user.client.id,
        socialMedia: socialMedia(user),
        payments: reverse(user.payments),
        link: '',
        convertedLink: '',
        toolbarState: ToolbarState.Idle(),
        deepLinkStatus: DeepLinkStatus.Idle(),
        userType: 'single',
      }
    },

    resetUser: (state) => {
      state.user = HTTP.Request.Done(state.originalUser)
    },

    setUser: (state, user) => {
      state.user = map(user, (user) => ({
        ...user,
        client_id: user.client.id,
        socialMedia: socialMedia(user),
        payments: reverse(user.payments),
        remove_earnings: false,
        link: '',
        convertedLink: '',
        toolbarState: ToolbarState.Idle(),
        deepLinkStatus: DeepLinkStatus.Idle(),
        userType: 'single',
      }))
    },

    setClients: (state, clients) => {
      state.clients = clients
    },

    setCheatReport: (state, report) => {
      state.cheatReport = report
    },

    setComments: (state, comments) => {
      state.comments = comments
    },

    setShowUser: (state, { id, value }) => {
      state.user = map(state.user, (user) => ({
        ...user,
        payments: map(user.payments, (payment) => {
          if (payment.id == id) {
            payment.show_user = value
          }
          return payment
        }),
      }))
    },

    setField: (state, { field, value }) => {
      state.user = map(state.user, (user) => update(user, field, value))
    },

    setCheatReportFilters: (state, { field, value }) => {
      state.CheatReportFilters = update(state.CheatReportFilters, field, value)
    },

    setUpdateStatus: (state, status) => {
      state.updateStatus = status
    },

    addEmptyOneTimeEarning: (state) => {
      state.oneTimeEarnings.push({
        id: 0,
        user_earnings: 0,
        invoice_amount: 0,
        payment_date: '',
        store_id: null,
        comment: '',
      })
    },

    updateOneTimeEarning: (state, { index, field, value }) => {
      state.oneTimeEarnings = state.oneTimeEarnings.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    },

    removeOneTimeEarning: (state, id) => {
      state.oneTimeEarnings = state.oneTimeEarnings.filter((item) => item.id !== id)
    },

    updateUsersList: (state, { path, value }) => {
      state.usersList[path] = value
    },

    setMergeStatus: (state, status) => {
      state.mergeUsersStatus = status
    },

    setToolbarStateUser(state, { state: toolbarState }) {
      state.user = map(state.user, (user) => ({
        ...user,
        toolbarState: toolbarState,
      }))
    },

    setDeepLinkStatus(state, { status }) {
      state.user = map(state.user, (user) => ({
        ...user,
        deepLinkStatus: status,
      }))
    },

    setLinkUser(state, { link }) {
      state.user = map(state.user, (user) => ({
        ...user,
        link,
      }))
    },

    setDeepLinkStatusUser(state, { status }) {
      state.user = map(state.user, (user) => ({
        ...user,
        deepLinkStatus: status,
      }))
    },

    setConvertedLinkUser(state, { convertedLink }) {
      state.user = map(state.user, (user) => ({
        ...user,
        convertedLink,
        deepLinkStatus: DeepLinkStatus.Done(),
      }))
    },

    setCopiedUser(state, { value }) {
      state.user = map(state.user, (user) => ({
        ...user,
        deepLinkCopied: value,
      }))
    },

    setOneTimeEarnings(state, value) {
      state.oneTimeEarnings = value
    },

    addOneTimeEarning(state, value) {
      state.oneTimeEarnings.push(value)
    },

    setOneTimeEarningsOriginal(state, value) {
      state.oneTimeEarningsOriginal = value
    },

    setErrorMessageList(state, value) {
      state.errorMessageList = value
    },

    setIsFetchingCheatReports(state, value) {
      state.isFetchingCheatReports = value
    },
  },

  getters: {
    user: ({ user }) => user,
    clients: ({ clients }) => clients,
    updateStatus: ({ updateStatus }) => updateStatus,
    cheatReport: ({ cheatReport }) => cheatReport,
    CheatReportFilters: ({ CheatReportFilters }) => CheatReportFilters,
    comments: ({ comments }) => comments,
    usersList: ({ usersList }) => usersList,
    mergeUsersStatus: ({ mergeUsersStatus }) => mergeUsersStatus,
    oneTimeEarnings: ({ oneTimeEarnings }) => oneTimeEarnings,
    userId: ({ user }) => caseOf(user, { Done: (user) => user.id, _: () => '' }),
    errorMessage: ({ errorMessageList }) => (errorMessageList.length ? errorMessageList : 'Something went wrong!'),
    isFetchingCheatReports: (state) => state.isFetchingCheatReports,
  },

  actions: {
    updateField: ({ commit }, { field, value }) => {
      commit('setField', { field, value })
    },

    updateCheatReportFilters: ({ commit }, { field, value }) => {
      commit('setCheatReportFilters', { field, value })
    },

    changeClient: ({ commit, getters }, value) => {
      commit('setField', { field: 'client_id', value })
      commit('setField', {
        field: 'revenue_tier_id',
        value: getters['clients'].find((client) => client.id === Number(value)).revenue_tiers[0].id,
      })
    },

    setUser: ({ commit }, user) => {
      commit('setOriginalUser', user)

      httpClient
        .get(`/v2/clients?size=300`)
        .then((response) => {
          commit('setUser', HTTP.Request.Done(user))
          const filteredClients = response.data.data.filter((c) => c.name)
          commit('setClients', filteredClients)
        })
        .catch((err) => commit('setUser', err))
    },

    init: ({ commit, dispatch }, id) => {
      commit('setUser', HTTP.Request.Pending())
      return httpClient
        .get(`/users/${id}`)
        .then((response) => {
          const userData = response.data
          if (userData.one_time_earnings && userData.one_time_earnings.length) {
            userData.one_time_earnings = userData.one_time_earnings.map((item) => ({
              ...item,
              invoice_amount: makeDecimalValue(item.invoice_amount),
              user_earnings: makeDecimalValue(item.user_earnings),
            }))
          }
          dispatch('setUser', userData)
        })
        .catch((err) => {
          commit('setUser', err)
        })
    },

    fetchComments: ({ commit, getters }) => {
      return map(getters.user, ({ id }) => {
        commit('setComments', Maybe.None())
        return httpClient
          .get(`/users/${id}/comments`)
          .then((response) => commit('setComments', Maybe.Some(response.data)))
          .catch((err) => console.log(err))
      })
    },

    addComment: ({ getters, dispatch, commit }, newComment) => {
      return map(getters.user, ({ id }) => {
        return httpClient
          .put(`/users/${id}/comments`, { comment: newComment })
          .then(() => dispatch('fetchComments'))
          .catch((err) => {
            errorHandler({ commit, message: 'Failed to add comment', error: err })
          })
      })
    },

    fetchCheatingReport: ({ commit, getters }, filters) => {
      commit('setIsFetchingCheatReports', true)
      const query = fold(
        maybe(filters),
        ({ startDate, endDate }) => `?from=${startDate}&to=${endDate}`,
        () => ``,
      )
      return map(getters.user, ({ id }) => {
        commit('setCheatReport', Maybe.None())
        return httpClient
          .get(`/clicks/cheatingReport/${id}${query}`)
          .then((response) => commit('setCheatReport', Maybe.Some(response.data)))
          .catch((err) => {
            console.log(err)
          })
          .finally(() => commit('setIsFetchingCheatReports', false))
      })
    },

    updateUser: ({ commit, state }) => {
      commit('setUpdateStatus', Status.Processing())
      commit('setErrorMessageList', [])
      const user = caseOf(state.user, { Done: (user) => user, _: () => ({}) })
      return httpClient
        .put(`/users/${user.id}`, serializeUserUpdate(user))
        .then(() => {
          commit('setUpdateStatus', Status.Updated())
          return true
        })
        .catch((error) => {
          const message = 'user update'
          errorHandler({ message, error, commit })
          commit('setUpdateStatus', Status.Failed())
        })
    },

    getOneTimeEarnings({ commit }, userId) {
      return httpClient
        .get(`/admin/users/${userId}/one-time-earnings`)
        .then((response) => {
          const { data } = response
          data.forEach((item) => {
            item.invoice_amount = item.invoice_amount / 100
            item.user_earnings = item.user_earnings / 100
          })
          commit('setOneTimeEarnings', data)
          commit('setOneTimeEarningsOriginal', [...data])
        })
        .catch((err) => console.error('Could not fetch one time earnings, ', err))
    },

    updateOneTimeEarnings({ commit, state, dispatch }) {
      commit('setErrorMessageList', [])
      const { oneTimeEarningsOriginal, oneTimeEarnings } = state
      const isEquals =
        oneTimeEarningsOriginal.length === oneTimeEarnings.length &&
        oneTimeEarningsOriginal.every((item, index) => JSON.stringify(oneTimeEarnings[index]) === JSON.stringify(item))
      if (isEquals) {
        return Promise.resolve(true)
      }
      const existedItemIds = oneTimeEarningsOriginal.map((item) => item.id)
      const newItems = oneTimeEarnings.filter((item) => !existedItemIds.includes(item.id))
      let promises = []
      if (newItems.length) {
        promises = newItems.map((item) => dispatch('createOneTimePayment', item))
      }
      const updatedItems = oneTimeEarnings.filter((item) => {
        if (existedItemIds.includes(item.id)) {
          const originalItem = oneTimeEarningsOriginal.find((originalItem) => originalItem.id === item.id)
          return JSON.stringify(originalItem) !== JSON.stringify(item)
        }
      })
      if (updatedItems.length) {
        promises = promises.concat(updatedItems.map((item) => dispatch('updateOneTimePayment', item)))
      }
      const allItemIds = oneTimeEarnings.map((item) => item.id)
      const removedItems = oneTimeEarningsOriginal.filter((item) => !allItemIds.includes(item.id))
      if (removedItems.length) {
        promises = promises.concat(removedItems.map((item) => dispatch('deleteOneTimePayment', item.id)))
      }
      if (promises.length) {
        commit('setUpdateStatus', Status.Processing())
        return Promise.all(promises)
          .then((res) => {
            if (newItems.length) {
              commit('setOneTimeEarnings', oneTimeEarningsOriginal)
              res.forEach((item) => {
                item.data.invoice_amount = item.data.invoice_amount / 100
                item.data.user_earnings = item.data.user_earnings / 100
                commit('addOneTimeEarning', item.data)
              })
            }
            commit('setOneTimeEarningsOriginal', [...state.oneTimeEarnings])
            commit('setUpdateStatus', Status.Updated())
          })
          .catch((error) => {
            const message = 'One time payment values'
            errorHandler({ error, commit, message })
            commit('setUpdateStatus', Status.Failed())
          })
      }
    },

    updateOneTimePayment({ getters }, oneTimeEarning) {
      const { userId } = getters
      const oneTimeEarningInCents = JSON.parse(JSON.stringify(oneTimeEarning))
      oneTimeEarningInCents.user_earnings = oneTimeEarningInCents.user_earnings ? oneTimeEarningInCents.user_earnings * 100 : null
      oneTimeEarningInCents.invoice_amount = oneTimeEarningInCents.invoice_amount ? oneTimeEarningInCents.invoice_amount * 100 : null
      return httpClient.put(`/admin/users/${userId}/one-time-earnings/${oneTimeEarningInCents.id}`, oneTimeEarningInCents)
    },

    createOneTimePayment({ getters }, oneTimeEarning) {
      const { userId } = getters
      const oneTimeEarningInCents = JSON.parse(JSON.stringify(oneTimeEarning))
      oneTimeEarningInCents.user_earnings = oneTimeEarningInCents.user_earnings ? oneTimeEarningInCents.user_earnings * 100 : null
      oneTimeEarningInCents.invoice_amount = oneTimeEarningInCents.invoice_amount ? oneTimeEarningInCents.invoice_amount * 100 : null
      oneTimeEarningInCents.user_id = userId
      return httpClient.post(`/admin/one-time-earnings`, oneTimeEarningInCents)
    },

    deleteOneTimePayment(_, id) {
      return httpClient.delete(`/admin/one-time-earnings/${id}`)
    },

    removeOneTimePayment: ({ commit }, id) => {
      commit('removeOneTimePayment', id)
    },

    reset: ({ commit }) => {
      commit('resetUser')
      commit('setErrorMessageList', [])
    },

    findUsers({ commit, state }, query) {
      findUsers.delayed(commit, state, query)
    },

    updateUsersList({ commit }, { path, value }) {
      commit('updateUsersList', { path, value })
    },

    toggleShowUser({ commit }, { id, oldValue }) {
      commit('setShowUser', { id, value: !oldValue })
      httpClient.get('/payments/' + id + '/toggleShowUser')
    },

    mergeUsers({ state, commit, getters }) {
      commit('setMergeStatus', HTTP.Request.Pending())
      commit('setErrorMessageList', [])
      map(state.user, (user) =>
        httpClient
          .post('/admin/users/' + user.id + '/merge/' + getters.usersList.selected.id)
          .then(() => {
            const message = 'Users merging is on queue. Users will be merged in a 1-2 min.'
            return commit('setMergeStatus', HTTP.Request.Done(message))
          })
          .catch((err) => {
            if (err.response && err.response.data.message) {
              commit('setErrorMessageList', [err.response.data.message])
            }
            commit('setMergeStatus', Status.Failed())
          }),
      )
    },

    gdprDelete({ state, commit, dispatch }) {
      if (confirm('Do you really want to delete this user according to GDPR standard?')) {
        commit('setUpdateStatus', Status.Processing())
        commit('setErrorMessageList', [])

        map(state.user, (user) =>
          httpClient
            .delete('/admin/users/' + user.id + '/gdpr-delete/')
            .then(() => {
              commit('setUpdateStatus', Status.Updated())

              dispatch('users/fetchUsers', {}, { root: true }).list

              setTimeout(() => {
                router.push({ name: 'Users' })
              }, 1500)
            })
            .catch((err) => {
              if (err.response && err.response.data.message) {
                commit('setErrorMessageList', [err.response.data.message])
              }
              commit('setUpdateStatus', Status.Failed())
            }),
        )
      }
    },
  },
}
